import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';

export default function ProtectedRoute({ children }) {
  const { auth, loading, sessionReady } = useContext(AuthContext);
  const location = useLocation();

  // 🌟 Shared branded container
  const BrandedScreen = ({ children }) => (
    <div className="min-h-screen w-full flex items-center justify-center bg-cream-color">
      <div
        className="
          bg-white/70 frosted-block
          p-8 md:p-10 rounded-xl shadow-lg w-[90%] max-w-sm
          border border-[#f5e8c8]
        "
      >
        <div className="flex flex-col items-center text-center space-y-4">

          {/* Brand Logo */}
          <img
            src={`${process.env.PUBLIC_URL}/logo.svg`}
            alt="Ruoth Logo"
            className="w-16 h-16 mb-2"
            style={{ objectFit: 'contain' }}
          />

          {children}
        </div>
      </div>
    </div>
  );

  // 1️⃣ Loading state (validating token / session)
  if (loading || !sessionReady) {
    return (
      <BrandedScreen>
        <div className="flex flex-col items-center space-y-3">
          <ShieldCheckIcon className="w-8 h-8 text-dark-purple opacity-80" />

          <p className="text-charcoal font-forms text-sm tracking-wide">
            Validating your session…
          </p>

          <div className="w-6 h-6 border-2 border-rich-gold border-t-transparent rounded-full animate-spin opacity-80"></div>
        </div>
      </BrandedScreen>
    );
  }

  // 2️⃣ Not authenticated → send user to login flow
  if (!auth.isAuthenticated) {
    return (
      <BrandedScreen>
        <div className="flex flex-col items-center space-y-3">

          <LockClosedIcon className="w-8 h-8 text-red-600 opacity-80" />

          <p className="text-red-600 font-forms text-sm tracking-wide">
            Access denied — you’re not logged in.
          </p>

          <p className="text-charcoal/70 text-xs font-forms">
            Redirecting you to the login page…
          </p>

          <div className="w-5 h-5 border-2 border-rich-gold border-t-transparent rounded-full animate-spin opacity-80"></div>
        </div>

        {/* Automatic redirect */}
        <Navigate to="/auth/login" state={{ from: location }} replace />
      </BrandedScreen>
    );
  }

  // 3️⃣ Authenticated — allow access
  return children;
}